import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase, DBSchema } from 'idb';
import { Project } from '../models/project.model';
import { Task } from '../models/task.model';
import { TimeEntry } from '../models/time-entry.model';
import { SyncEvent } from '../models/sync-event.model';

interface TimeTrackerSchema extends DBSchema {
  projects: { key: number; value: Project };
  tasks: {
    key: number;
    value: Task;
    indexes: { 'by-project': number };
  };
  'time-entries': {
    key: number;
    value: TimeEntry;
    indexes: { 'by-user': number; 'by-project': number; 'by-start': string };
  };
  'sync-events': {
    key: string;
    value: SyncEvent;
    indexes: { 'by-state': string };
  };
  auth: { key: string; value: string };
}

@Injectable({ providedIn: 'root' })
export class DbService {
  private db!: IDBPDatabase<TimeTrackerSchema>;

  async open(): Promise<void> {
    this.db = await openDB<TimeTrackerSchema>('timetracker', 1, {
      upgrade(db) {
        db.createObjectStore('projects', { keyPath: 'id' });

        const tasks = db.createObjectStore('tasks', { keyPath: 'id' });
        tasks.createIndex('by-project', 'project_id');

        const entries = db.createObjectStore('time-entries', { keyPath: 'id' });
        entries.createIndex('by-user', 'user_id');
        entries.createIndex('by-project', 'project_id');
        entries.createIndex('by-start', 'start_time');

        const events = db.createObjectStore('sync-events', { keyPath: 'id' });
        events.createIndex('by-state', 'state');

        db.createObjectStore('auth');
      },
    });
  }

  private get store(): IDBPDatabase<TimeTrackerSchema> {
    return this.db;
  }

  // Auth
  async getToken(): Promise<string | undefined> {
    return this.store.get('auth', 'jwt');
  }
  async setToken(token: string): Promise<void> {
    await this.store.put('auth', token, 'jwt');
  }
  async clearToken(): Promise<void> {
    await this.store.delete('auth', 'jwt');
  }

  // Projects (with nested tasks)
  async getProjects(): Promise<Project[]> {
    return this.store.getAll('projects');
  }
  async getProjectsByUser(userId: number): Promise<Project[]> {
    const all = await this.store.getAll('projects');
    return all.filter(p => p.user_id === userId);
  }
  async putProject(p: Project): Promise<void> {
    await this.store.put('projects', p);
  }
  async putProjects(projects: Project[]): Promise<void> {
    const tx = this.store.transaction('projects', 'readwrite');
    await Promise.all([...projects.map(p => tx.store.put(p)), tx.done]);
  }
  async deleteProject(id: number): Promise<void> {
    await this.store.delete('projects', id);
  }

  // Helper: sync tasks into a project
  async syncProjectTasks(projectId: number, tasks: Task[]): Promise<void> {
    const tx = this.store.transaction(['projects', 'tasks'], 'readwrite');
    const project = await tx.objectStore('projects').get(projectId);
    if (project) {
      project.tasks = tasks;
      await tx.objectStore('projects').put(project);
    }
    await Promise.all(tasks.map(t => tx.objectStore('tasks').put(t)));
    await tx.done;
  }

  // Tasks
  async getTasks(): Promise<Task[]> {
    return this.store.getAll('tasks');
  }
  async getTasksByProject(projectId: number): Promise<Task[]> {
    return this.store.getAllFromIndex('tasks', 'by-project', projectId);
  }
  async putTask(t: Task): Promise<void> {
    await this.store.put('tasks', t);
  }
  async putTasks(tasks: Task[]): Promise<void> {
    const tx = this.store.transaction('tasks', 'readwrite');
    await Promise.all([...tasks.map(t => tx.store.put(t)), tx.done]);
  }
  async deleteTask(id: number): Promise<void> {
    await this.store.delete('tasks', id);
  }

  // Time entries
  async getTimeEntries(): Promise<TimeEntry[]> {
    return this.store.getAll('time-entries');
  }
  async getTimeEntriesByUser(userId: number): Promise<TimeEntry[]> {
    return this.store.getAllFromIndex('time-entries', 'by-user', userId);
  }
  async putTimeEntry(e: TimeEntry): Promise<void> {
    await this.store.put('time-entries', e);
  }
  async putTimeEntries(entries: TimeEntry[]): Promise<void> {
    const tx = this.store.transaction('time-entries', 'readwrite');
    await Promise.all([...entries.map(e => tx.store.put(e)), tx.done]);
  }
  async replaceTimeEntries(entries: TimeEntry[]): Promise<void> {
    const tx = this.store.transaction('time-entries', 'readwrite');
    await tx.store.clear();
    await Promise.all([...entries.map(e => tx.store.put(e)), tx.done]);
  }
  async deleteTimeEntry(id: number): Promise<void> {
    await this.store.delete('time-entries', id);
  }

  // Sync events
  async getPendingSyncEvents(): Promise<SyncEvent[]> {
    return this.store.getAllFromIndex('sync-events', 'by-state', 'pending');
  }
  async getFailedSyncEvents(): Promise<SyncEvent[]> {
    return this.store.getAllFromIndex('sync-events', 'by-state', 'failed');
  }
  async resetFailedSyncEvents(): Promise<void> {
    const tx = this.store.transaction('sync-events', 'readwrite');
    const failed = await tx.store.index('by-state').getAll('failed');
    await Promise.all(failed.map(ev => { ev.state = 'pending'; return tx.store.put(ev); }));
    await tx.done;
  }
  async putSyncEvent(e: SyncEvent): Promise<void> {
    await this.store.put('sync-events', e);
  }
  async updateSyncEventState(id: string, state: SyncEvent['state']): Promise<void> {
    const tx = this.store.transaction('sync-events', 'readwrite');
    const event = await tx.store.get(id);
    if (event) {
      event.state = state;
      await tx.store.put(event);
    }
    await tx.done;
  }
  async deleteSyncEvent(id: string): Promise<void> {
    await this.store.delete('sync-events', id);
  }
}
